import { encode as jsquashEncode } from '@jsquash/webp';
import { LOCAL_ORIGIN, type ObjectData, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import { THUMB_SIZE, type Api } from '../../lib/api';
import { coverSource } from '../objects/image-fit';
import { canEncodeWebp } from './encode';

export type ThumbOp = {
  kind: 'rect' | 'ellipse' | 'text' | 'image' | 'line';
  x: number;
  y: number;
  w: number;
  h: number;
  fill?: string;
  assetId?: string;
  crop?: { x: number; y: number; w: number; h: number };
  points?: Point[];
};

const MARGIN = 24;
const QUALITY = 0.8;
const BACKGROUND = '#f6f3ee';
const NEUTRAL = '#d9d4cc';
const INK = '#8a847b';

const fillOf = (o: ObjectData): string | undefined => {
  if (o.type === 'sticky') return o.color;
  const fill = o.style.fill;
  return typeof fill === 'string' ? fill : undefined;
};

/** A page's objects (the active one by default) at their world positions: children of sections and groups are stored relative to them. */
export function worldObjects(store: SceneStore, pageId: string = store.page): ObjectData[] {
  const { world } = store.graphOf(pageId);
  return store.listPage(pageId).map((o) => {
    const w = world.get(o.id);
    return w ? { ...o, x: w.x, y: w.y } : o;
  });
}

/** What the board's preview shows: its first page, whichever page the user is on (P14). */
export const boardThumbnailObjects = (store: SceneStore): ObjectData[] => worldObjects(store, store.pages()[0]!.id);

/** The board drawn as a 640 × 400 preview (spec §8): content centred with a 24 px margin, never enlarged, in z order. */
export function thumbnailPlan(objects: ReadonlyArray<ObjectData>, size: { w: number; h: number } = THUMB_SIZE): ThumbOp[] {
  if (objects.length === 0) return [];
  const minX = Math.min(...objects.map((o) => o.x));
  const minY = Math.min(...objects.map((o) => o.y));
  const bw = Math.max(1, Math.max(...objects.map((o) => o.x + o.w)) - minX);
  const bh = Math.max(1, Math.max(...objects.map((o) => o.y + o.h)) - minY);
  const k = Math.min(1, (size.w - 2 * MARGIN) / bw, (size.h - 2 * MARGIN) / bh);
  const ox = (size.w - bw * k) / 2 - minX * k;
  const oy = (size.h - bh * k) / 2 - minY * k;
  return [...objects]
    .sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0))
    .map((o): ThumbOp => {
      const box = { x: ox + o.x * k, y: oy + o.y * k, w: o.w * k, h: o.h * k };
      if (o.type === 'image') return { kind: 'image', ...box, assetId: o.assetId, crop: o.crop };
      if (o.type === 'text') return { kind: 'text', ...box, fill: INK };
      if (o.type === 'arrow') return { kind: 'line', ...box, fill: INK, points: o.points.map((p) => ({ x: box.x + p.x * k, y: box.y + p.y * k })) };
      if (o.type === 'shape' && o.shape === 'ellipse') return { kind: 'ellipse', ...box, fill: fillOf(o) ?? NEUTRAL };
      return { kind: 'rect', ...box, fill: fillOf(o) ?? NEUTRAL };
    });
}

type Canvas2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

/** Draws the plan and encodes WebP q 0.8 (native, else @jsquash/webp). Images are requested at once, before any await. */
export async function renderThumbnail(
  ops: ThumbOp[],
  load: (assetId: string) => Promise<ImageBitmap | null>,
  size: { w: number; h: number } = THUMB_SIZE,
  background: string = BACKGROUND,
): Promise<Blob> {
  const bitmaps = ops.map((op) => (op.kind === 'image' && op.assetId ? load(op.assetId).catch(() => null) : Promise.resolve(null)));
  const canvas = new OffscreenCanvas(size.w, size.h);
  const ctx: Canvas2D = canvas.getContext('2d')!;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, size.w, size.h);
  for (const [i, op] of ops.entries()) {
    ctx.fillStyle = op.fill ?? NEUTRAL;
    if (op.kind === 'image') {
      const bitmap = await bitmaps[i];
      if (bitmap) {
        // The crop region covering the box: never the whole source stretched into it.
        const s = coverSource({ w: bitmap.width, h: bitmap.height }, op.crop ?? { x: 0, y: 0, w: 1, h: 1 }, op);
        ctx.drawImage(bitmap, s.sx, s.sy, s.sw, s.sh, op.x, op.y, op.w, op.h);
      }
      else ctx.fillRect(op.x, op.y, op.w, op.h);
    } else if (op.kind === 'ellipse') {
      ctx.beginPath();
      ctx.ellipse(op.x + op.w / 2, op.y + op.h / 2, op.w / 2, op.h / 2, 0, 0, 2 * Math.PI);
      ctx.fill();
    } else if (op.kind === 'text') {
      for (let y = op.y; y < op.y + op.h; y += 6) ctx.fillRect(op.x, y, op.w * 0.8, Math.min(3, op.y + op.h - y));
    } else if (op.kind === 'line' && op.points?.length) {
      ctx.strokeStyle = op.fill ?? INK;
      ctx.beginPath();
      op.points.forEach((p, j) => (j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    } else ctx.fillRect(op.x, op.y, op.w, op.h);
  }
  if (await canEncodeWebp()) return canvas.convertToBlob({ type: 'image/webp', quality: QUALITY });
  const buf = await jsquashEncode(ctx.getImageData(0, 0, size.w, size.h), { quality: Math.round(QUALITY * 100) });
  return new Blob([buf], { type: 'image/webp' });
}

/**
 * Regenerates the board's preview when the user leaves it or hides the tab (never on beforeunload), only after a
 * local edit and only while `enabled()` (editor and up). One upload in flight at a time; a failure keeps it due.
 * Returns the stop function, which uploads a pending change.
 */
export function watchBoardThumbnail(o: {
  store: SceneStore;
  boardId: string;
  api: Pick<Api, 'putBoardThumbnail'>;
  load: (assetId: string) => Promise<ImageBitmap | null>;
  enabled: () => boolean;
  render?: (ops: ThumbOp[]) => Promise<Blob>;
  /** After a stored preview (e.g. invalidate the dashboard's cached thumbnail). */
  onUploaded?: () => void;
}): () => void {
  let dirty = false;
  let inflight = false;
  // The preview shows the board's own background color (white by default, backlog §3).
  const render = o.render ?? ((ops: ThumbOp[]) => renderThumbnail(ops, o.load, THUMB_SIZE, o.store.meta().background.color));
  const onUpdate = (_u: Uint8Array, origin: unknown) => {
    if (origin === LOCAL_ORIGIN) dirty = true;
  };
  const upload = () => {
    if (!dirty || inflight || !o.enabled()) return;
    dirty = false;
    const ops = thumbnailPlan(boardThumbnailObjects(o.store));
    if (ops.length === 0) return;
    inflight = true;
    render(ops)
      .then((blob) => o.api.putBoardThumbnail(o.boardId, blob))
      .then(() => o.onUploaded?.())
      .catch(() => {
        dirty = true;
      })
      .finally(() => {
        inflight = false;
      });
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') upload();
  };
  o.store.doc.on('update', onUpdate);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    o.store.doc.off('update', onUpdate);
    document.removeEventListener('visibilitychange', onVisibility);
    upload();
  };
}
